import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { AiOutlineHome, AiOutlineMenu, AiOutlineClose, AiOutlineApi, AiOutlineFileText } from 'react-icons/ai';
import { BsBriefcase, BsPerson, BsSun, BsMoonStars } from 'react-icons/bs';
import { FaRobot, FaBriefcase } from 'react-icons/fa';
import { GoTools } from 'react-icons/go';
import { VscProject } from 'react-icons/vsc';
import { TbSphere } from 'react-icons/tb';
import { useTheme } from '../context/ThemeContext';
import styles from './Sidebar.module.css';

const Sidebar: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleResize = useCallback(() => {
    if (window.innerWidth > 768) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleMenu = useCallback(() => setIsOpen(prev => !prev), []);
  const navItems = useMemo(() => [
    { path: '/', label: 'Home', icon: <AiOutlineHome /> },
    { path: '/services', label: 'Services', icon: <FaBriefcase /> },
    { path: '/commercial-disclosure', label: 'Commercial Disclosure', icon: <AiOutlineFileText /> },
    { path: '/code-editor-resume', label: 'Code Editor Resume', icon: <GoTools /> },
    { path: '/terminal-resume', label: 'Terminal Resume', icon: <VscProject /> },
    { path: '/api-explorer', label: 'API Explorer', icon: <AiOutlineApi /> },
    { path: '/kanban-resume', label: 'Kanban Resume', icon: <BsBriefcase /> },
    { path: '/chatbot-resume', label: 'Chatbot Resume', icon: <FaRobot /> },
    { path: '/skills-sphere', label: '3D Skills Sphere', icon: <TbSphere /> },
  ], []);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <button
        className={`${styles.menuButton} ${isOpen ? styles.menuButtonOpen : ''}`}
        onClick={toggleMenu}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
      >
        <span className={styles.hamburgerIcon}>
          {isOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
        </span>
      </button>

      <aside
        className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}
        role="complementary"
        aria-hidden={!isOpen}
      >
        <div className={styles.logo} role="banner">
          <h2>Portfolio</h2>
        </div>
        <nav className={styles.nav} aria-label="Main navigation">
          <ul>
            <li>
              <button
                onClick={toggleTheme}
                className={styles.themeToggle}
                aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                aria-pressed={isDark}
              >
                <span className={styles.icon} aria-hidden="true">
                  {isDark ? <BsSun /> : <BsMoonStars />}
                </span>
                <span className={styles.label}>
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </span>
              </button>
            </li>
            {navItems.map(item => (
              <li key={item.path} className={styles.navItem}>
                <NavLink
                  exact
                  to={item.path}
                  className={styles.navLink}
                  activeClassName={styles.navLinkActive}
                  aria-label={item.label} onClick={closeMenu}
                >
                  <span className={styles.icon} aria-hidden="true">{item.icon}</span>
                  <span className={styles.label}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Animated overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
        onClick={toggleMenu}
        aria-hidden="true"
      />
    </>
  );
};

export default Sidebar;